import React, { useEffect, useRef, useState } from 'react';
import { computeTrueTotals, formatQuestionLabel, normalizeQuestionId } from '../utils/tableToOneLine';
import { extractRollNumber } from '../utils/marksValidation';
import { GradingMachineBadge, GRADING_MACHINE_LEGEND } from '../utils/gradingMachine';
import { folderDisplayTitle } from '../utils/folderDisplay';
import StudentResultView from './StudentResultView';
import RubricQuestionPanel from './RubricQuestionPanel';

function classSheetLabel(folder) {
  if (!folder) return '';
  const base = folderDisplayTitle(folder);
  const assignment = String(folder.assignment_name || '').trim();
  let dateStr = '';
  if (folder.test_date) {
    const date = new Date(folder.test_date);
    if (!Number.isNaN(date.getTime())) {
      dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
  return [base, assignment, dateStr].filter(Boolean).join(' · ');
}

const ROMAN = {
  i: 1, ii: 2, iii: 3, iv: 4, v: 5, vi: 6,
  vii: 7, viii: 8, ix: 9, x: 10, xi: 11, xii: 12
};

function splitIdentity(raw) {
  const s = String(raw || '').replace(/\s+/g, ' ').trim();
  const roll = (extractRollNumber(s) || '').trim();
  if (!roll) return { roll: '', name: s || 'Unknown' };
  const name = s.replace(`(${roll})`, '').replace(/\s+/g, ' ').trim();
  return { roll, name: name || s };
}

function questionRank(id) {
  const n = normalizeQuestionId(id) || '';
  const m = n.match(/^q(\d+)(?:\.(.+))?$/);
  if (!m) return [9999, 9999, String(id || '')];
  const sub = m[2] || '';
  let subRank = 0;
  if (sub) {
    if (ROMAN[sub] != null) subRank = ROMAN[sub];
    else if (/^\d+$/.test(sub)) subRank = 50 + parseInt(sub, 10);
    else subRank = 200 + sub.charCodeAt(0);
  }
  return [parseInt(m[1], 10), subRank, formatQuestionLabel(id)];
}

function compareQuestions(a, b) {
  const ka = questionRank(a);
  const kb = questionRank(b);
  for (let i = 0; i < 3; i++) {
    if (ka[i] < kb[i]) return -1;
    if (ka[i] > kb[i]) return 1;
  }
  return 0;
}

function fmtNum(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const r = Math.round(Number(n) * 100) / 100;
  return Number.isInteger(r) ? String(r) : String(r);
}

function classPercentPoints(obtained, max) {
  if (!max) return null;
  return Math.round((obtained / max) * 1000) / 10;
}

function fmtPct(obtained, max) {
  const points = classPercentPoints(obtained, max);
  if (points == null) return '—';
  return `${points.toFixed(1)}%`;
}

// 1 is the highest class percentage. Equal percentages share a rank, and the
// next distinct percentage takes the following place (90, 90, 80 → 1, 1, 3).
function assignClassRanks(classStats) {
  const order = classStats
    .map((stat, index) => ({ index, pct: classPercentPoints(stat.obtained, stat.max) }))
    .filter((item) => item.pct != null)
    .sort((a, b) => b.pct - a.pct || a.index - b.index);
  const ranks = classStats.map(() => null);
  let place = 0;
  let prev = null;
  order.forEach((item, seen) => {
    if (prev == null || item.pct !== prev) place = seen + 1;
    prev = item.pct;
    ranks[item.index] = place;
  });
  return classStats.map((stat, index) => ({ ...stat, rank: ranks[index] }));
}

// Rank 1 and scores above 90 stay light slate. Other questions follow the class
// percentage: below 50 rose, 50–60 orange, 61–70 amber, 71–90 blue.
const PERCENT_BANDS = {
  red: { header: '#ffe4e6', border: '#fecdd3', rank: '#fff1f2', color: '#9f1239', rankColor: '#9f1239' },
  orange: { header: '#ffedd5', border: '#fed7aa', rank: '#fff7ed', color: '#9a3412', rankColor: '#9a3412' },
  yellow: { header: '#fef3c7', border: '#fde68a', rank: '#fffbeb', color: '#854d0e', rankColor: '#854d0e' },
  blue: { header: '#dbeafe', border: '#bfdbfe', rank: '#eff6ff', color: '#1e3a8a', rankColor: '#1e3a8a' }
};

function percentBand(stat) {
  if (!stat || stat.rank === 1) return null;
  const pct = classPercentPoints(stat.obtained, stat.max);
  if (pct == null) return null;
  if (pct < 50) return 'red';
  if (pct < 61) return 'orange';
  if (pct < 71) return 'yellow';
  if (pct <= 90) return 'blue';
  return null;
}

function rubricStatements(text) {
  const map = new Map();
  const lines = String(text || '').split('\n');
  const headerRe = /^(Q\.?\s*\d+(?:\s*[.)]\s*[A-Za-z0-9]+)?)\b(.*)$/i;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const match = line.match(headerRe);
    if (!match) continue;
    if (/rubrics?\s*breakdown|sub-?step\s*table|model\s*answer\s*:/i.test(line)) continue;
    const looksLikeHeader = /\(\s*\d+(?:\.\d+)?\s*marks?\s*\)/i.test(line)
      || /^Q\.?\s*\d+\s*[.)]\s*[A-Za-z0-9]+/i.test(line)
      || /^Q\.?\s*\d+\s*[-–—:]/i.test(line);
    if (!looksLikeHeader) continue;
    const id = normalizeQuestionId(match[1]);
    if (!id || map.has(id)) continue;
    const parts = [];
    const rest = match[2]
      .replace(/^[\s.:)\-–—]+/, '')
      .replace(/\(\s*\d+(?:\.\d+)?\s*marks?\s*\)\s*$/i, '')
      .trim();
    if (rest) parts.push(rest);
    for (let j = i + 1; j < lines.length; j++) {
      const next = lines[j].trim();
      if (!next) break;
      if (/^Q\.?\s*\d+/i.test(next)) break;
      if (/^(model\s*answer|rubrics?\s*breakdown|sub-?step|marking\s*scheme)\b/i.test(next)) break;
      parts.push(next);
      if (parts.join(' ').length > 500) break;
    }
    const statement = parts.join(' ').replace(/\s+/g, ' ').trim();
    if (statement) map.set(id, statement);
  }
  return map;
}

function sortMark(student, key) {
  if (key === 'total') return student.obtained;
  if (key === 'percent') {
    if (student.obtained == null || !student.max) return null;
    return student.obtained / student.max;
  }
  const cell = student.byQuestion.get(key);
  return cell ? cell.obtained : null;
}

function sortStudents(students, sort) {
  if (!sort) return students;
  const byRoll = (a, b) => compareRoll(a.roll, b.roll) || a.name.localeCompare(b.name);
  if (sort.key === 'roll') {
    const dir = sort.dir === 'desc' ? -1 : 1;
    return [...students].sort((a, b) => dir * byRoll(a, b));
  }
  return [...students].sort((a, b) => {
    const av = sortMark(a, sort.key);
    const bv = sortMark(b, sort.key);
    const aMissing = av == null || Number.isNaN(av);
    const bMissing = bv == null || Number.isNaN(bv);
    if (aMissing || bMissing) {
      if (aMissing && bMissing) return byRoll(a, b);
      return aMissing ? 1 : -1;
    }
    if (av !== bv) return sort.dir === 'asc' ? av - bv : bv - av;
    return byRoll(a, b);
  });
}

function rollParts(roll) {
  const s = String(roll || '').trim().toUpperCase();
  const match = s.match(/^([A-Z]*)(\d*)/);
  const letters = (match && match[1]) || '';
  const digits = (match && match[2]) || '';
  const n = digits ? parseInt(digits, 10) : Number.MAX_SAFE_INTEGER;
  return {
    letters: letters || '\uffff',
    n: Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
  };
}

function compareRoll(a, b) {
  const pa = rollParts(a);
  const pb = rollParts(b);
  if (pa.letters !== pb.letters) return pa.letters < pb.letters ? -1 : 1;
  return pa.n - pb.n;
}

function parseStudent(row) {
  const parsed = row.table_comments ? computeTrueTotals(row.table_comments) : null;
  const byQuestion = new Map();
  if (parsed) {
    for (const sq of parsed.subQuestions) {
      if (sq.isExtra) continue;
      const key = normalizeQuestionId(sq.id) || formatQuestionLabel(sq.id);
      byQuestion.set(key, { obtained: sq.obtained, max: sq.max, deduction: sq.deduction || '' });
    }
  }
  const identity = splitIdentity(row.student_name);
  const grand = parsed ? parsed.grandTotal : null;
  const marksMatch = String(row.marks ?? '').trim().match(/^(\d+(?:\.\d+)?)/);
  const fallback = marksMatch ? parseFloat(marksMatch[1]) : null;
  return {
    key: identity.roll ? `${identity.roll}|${identity.name}` : identity.name.toLowerCase(),
    roll: identity.roll,
    name: identity.name,
    studentName: String(row.student_name || '').trim(),
    row,
    byQuestion,
    obtained: grand ? grand.obtained : (Number.isFinite(fallback) ? fallback : null),
    max: grand ? grand.max : null,
    scored: byQuestion.size > 0 || grand != null || fallback != null
  };
}

function samePerson(a, b) {
  const nameA = String(a || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const nameB = String(b || '').replace(/\s+/g, ' ').trim().toLowerCase();
  if (nameA && nameA === nameB) return true;
  const rollA = (extractRollNumber(a) || '').trim();
  const rollB = (extractRollNumber(b) || '').trim();
  return !!(rollA && rollB && rollA === rollB);
}

function liveFor(row, liveRows) {
  const hits = (liveRows || []).filter((r) => r && samePerson(r.student_name, row && row.student_name));
  return hits.find((r) => r.table_comments) || hits[0] || null;
}

function nextImageFilename(name) {
  const match = String(name).match(/^(.*?)(\d+)(\.[^.]+)?$/);
  if (!match) return null;
  const nextNum = String(parseInt(match[2], 10) + 1).padStart(match[2].length, '0');
  return `${match[1]}${nextNum}${match[3] || ''}`;
}

function rowMatchesRoll(row, code) {
  if (!row || !code) return false;
  const unique = row.unique_code != null ? String(row.unique_code).trim().toLowerCase() : '';
  if (unique && unique !== '000' && unique === code) return true;
  const fromName = (extractRollNumber(row.student_name || row.roll_name) || '').trim().toLowerCase();
  return fromName === code;
}

// Same gallery as the Assessment tab: every sorted page for this roll, and for
// each front the next file (the back of a double-sided scan). A graded result
// stores only the first front. The live sort list is kept per login, so a
// class sorted by someone else — or already cleared from this login — is read
// from the folder snapshot, which is what Assessment imports before opening.
function galleryNames(studentName, pools) {
  const code = (extractRollNumber(studentName) || '').trim().toLowerCase();
  if (!code) return [];
  const seen = new Set();
  const names = [];
  const add = (name) => {
    const text = String(name || '').trim();
    const key = text.toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    names.push(text);
  };
  for (const pool of pools) {
    for (const row of pool || []) {
      if (!rowMatchesRoll(row, code)) continue;
      const raw = row.image_name || row.image_no || '';
      String(raw).split(',').forEach((part) => {
        const front = part.trim();
        if (!front) return;
        add(front);
        const back = nextImageFilename(front);
        if (back && back !== front) add(back);
      });
    }
  }
  names.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  return names;
}

function buildSheet(rows, liveRows) {
  const byStudent = new Map();
  for (const row of rows || []) {
    const student = parseStudent(row);
    if (!student.scored && !student.name) continue;
    const prev = byStudent.get(student.key);
    if (!prev || student.byQuestion.size >= prev.byQuestion.size) byStudent.set(student.key, student);
  }
  const students = [...byStudent.values()].filter((s) => s.scored);
  students.forEach((student) => {
    student.liveRow = liveFor(student.row, liveRows);
  });
  students.sort((a, b) => compareRoll(a.roll, b.roll) || a.name.localeCompare(b.name));

  const keys = new Set();
  for (const student of students) {
    for (const key of student.byQuestion.keys()) keys.add(key);
  }
  const questions = [...keys].sort(compareQuestions);
  const classStats = questions.map((key) => {
    let obtained = 0;
    let max = 0;
    for (const student of students) {
      const cell = student.byQuestion.get(key);
      if (!cell || !cell.max) continue;
      obtained += cell.obtained;
      max += cell.max;
    }
    return { key, label: formatQuestionLabel(key), obtained, max };
  });
  const rankedStats = assignClassRanks(classStats);
  let classObtained = 0;
  let classMax = 0;
  for (const student of students) {
    if (student.obtained == null || !student.max) continue;
    classObtained += student.obtained;
    classMax += student.max;
  }
  return { students, classStats: rankedStats, classObtained, classMax };
}

function TriCheckbox({ checked, indeterminate, onChange, title, label, light }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <label
      onClick={(event) => event.stopPropagation()}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 11,
        color: light ? '#fff' : '#0f172a',
        lineHeight: 1.1
      }}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ width: 15, height: 15, margin: 0, accentColor: '#0f172a', cursor: 'pointer' }}
      />
      {label}
    </label>
  );
}

export default function QuestionReportSheet({ folder, title, onClose, onReviewChange, onSwitchFolder }) {
  const [state, setState] = useState({ status: 'loading', sheet: null, rows: [], liveRows: [], analyzedRows: [], message: '' });
  const [peek, setPeek] = useState(null);
  const [questionFocus, setQuestionFocus] = useState(null);
  const [sort, setSort] = useState(null);
  const [statements, setStatements] = useState(() => new Map());
  const [rubricText, setRubricText] = useState('');
  const [rubricSubject, setRubricSubject] = useState('');
  const [rubricFocus, setRubricFocus] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState(() => new Set());
  const [selectedQuestions, setSelectedQuestions] = useState(() => new Set());
  const [resultOk, setResultOk] = useState(false);
  const [regradeRequests, setRegradeRequests] = useState([]);
  const [actionBusy, setActionBusy] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [audioError, setAudioError] = useState('');
  const [noteText, setNoteText] = useState('');
  const [showGuide, setShowGuide] = useState(true);
  const [classFolders, setClassFolders] = useState([]);
  const [classQuery, setClassQuery] = useState('');
  const [classMenuOpen, setClassMenuOpen] = useState(false);
  const [classMenuPos, setClassMenuPos] = useState(null);
  const classBoxRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const audioUrlRef = useRef('');

  const releaseRecording = () => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
    recorderRef.current = null;
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach((track) => track.stop());
      recordStreamRef.current = null;
    }
  };

  useEffect(() => () => {
    releaseRecording();
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = e.target && e.target.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') return;
      if (showGuide) {
        setShowGuide(false);
        return;
      }
      if (peek) {
        setPeek(null);
        return;
      }
      if (questionFocus) {
        setQuestionFocus(null);
        return;
      }
      if (rubricFocus) {
        setRubricFocus(null);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, rubricFocus, peek, questionFocus, showGuide]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSort(null);
      setStatements(new Map());
      setRubricText('');
      setRubricSubject('');
      setRubricFocus(null);
      setHoverTip(null);
      setPeek(null);
      setQuestionFocus(null);
      setSelectedStudents(new Set());
      setSelectedQuestions(new Set());
      setActionNote('');
      setAudioError('');
      setAudioFile(null);
      setRecording(false);
      setRecordSecs(0);
      releaseRecording();
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = '';
      }
      setAudioUrl('');
      setNoteText('');
      setClassQuery('');
      setClassMenuOpen(false);
      setShowGuide(true);
      setResultOk(false);
      setRegradeRequests([]);
      setState({ status: 'loading', sheet: null, rows: [], liveRows: [], analyzedRows: [], message: '' });
      const token = localStorage.getItem('authToken');
      const folderPath = `rubrics/${folder.id}`;
      const headers = { Authorization: `Bearer ${token}` };
      const base = window.API_BASE_URL;
      const reviewPromise = fetch(`${base}/analyze/folders/${folder.id}/review`, { headers })
        .then(async (res) => {
          const body = await res.json().catch(() => ({}));
          if (!res.ok || !body.success) return null;
          return body;
        })
        .catch(() => null);
      const rubricPromise = fetch(`${base}/rubrics/${folder.id}`, { headers })
        .then(async (res) => {
          const body = await res.json().catch(() => ({}));
          const rubric = res.ok && body.rubric ? body.rubric : null;
          const text = rubric ? (rubric.content || '') : '';
          return { text, subject: rubric ? (rubric.subject || '') : '', map: rubricStatements(text) };
        })
        .catch(() => ({ text: '', subject: '', map: new Map() }));
      try {
        const [snapRes, liveRes, analyzedRes, sortRes] = await Promise.all([
          fetch(`${base}/assess/snapshots?folder_path=${encodeURIComponent(folderPath)}`, { headers }),
          fetch(`${base}/assess/results?folder_path=${encodeURIComponent(folderPath)}`, { headers }),
          fetch(`${base}/analyze/results?folder_path=${encodeURIComponent(folderPath)}`, { headers }),
          fetch(`${base}/analyze/snapshots?folder_path=${encodeURIComponent(folderPath)}`, { headers })
        ]);
        const snap = await snapRes.json().catch(() => ({}));
        const live = await liveRes.json().catch(() => ({}));
        const analyzed = await analyzedRes.json().catch(() => ({}));
        const sorted = await sortRes.json().catch(() => ({}));
        const snapRows = snapRes.ok && Array.isArray(snap.data) ? snap.data : [];
        const liveRows = liveRes.ok && Array.isArray(live.data)
          ? live.data
          : (liveRes.ok && Array.isArray(live.results) ? live.results : []);
        const analyzedRows = analyzedRes.ok && Array.isArray(analyzed.data) ? analyzed.data : [];
        const sortRows = sortRes.ok && Array.isArray(sorted.data) ? sorted.data : [];
        if (sortRows.length) analyzedRows.push(...sortRows);
        const rows = snapRows.some((r) => r && r.table_comments) ? snapRows : liveRows;
        if (cancelled) return;
        const loadedRubric = await rubricPromise;
        const loadedReview = await reviewPromise;
        if (!cancelled) {
          setStatements(loadedRubric.map);
          setRubricText(loadedRubric.text || '');
          setRubricSubject(loadedRubric.subject || folder.subject || '');
          if (loadedReview) {
            setResultOk(!!loadedReview.result_ok);
            setRegradeRequests(Array.isArray(loadedReview.requests) ? loadedReview.requests : []);
            if (onReviewChange) onReviewChange(loadedReview);
          }
        }
        const sheet = buildSheet(rows, liveRows);
        if (!sheet.students.length) {
          setState({ status: 'empty', sheet: null, rows: [], liveRows: [], analyzedRows, message: 'No checked marks are saved for this folder yet.' });
          return;
        }
        setState({ status: 'ok', sheet, rows, liveRows, analyzedRows, message: '' });
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', sheet: null, rows: [], liveRows: [], analyzedRows: [], message: err.message || 'Could not load the report.' });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [folder.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${window.API_BASE_URL}/scans/folders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setClassFolders(Array.isArray(data.folders) ? data.folders : []);
      } catch (_) { /* the class box stays empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!classMenuOpen) return undefined;
    const place = () => {
      const node = classBoxRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      setClassMenuPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(420, rect.width) });
    };
    const onDown = (event) => {
      if (classBoxRef.current && !classBoxRef.current.contains(event.target)) setClassMenuOpen(false);
    };
    place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [classMenuOpen]);

  const sheet = state.sheet;
  const shownStudents = sheet ? sortStudents(sheet.students, sort) : [];

  const toggleSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: key === 'roll' ? 'asc' : 'desc' };
      return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
    });
  };

  const sortArrow = (key) => {
    if (!sort || sort.key !== key) return '';
    return sort.dir === 'desc' ? ' ↓' : ' ↑';
  };

  const openStudentComments = (student) => {
    const sheetRow = student.row;
    const live = student.liveRow;
    if (!sheetRow && !live) return;
    setHoverTip(null);
    const imageNames = galleryNames((sheetRow && sheetRow.student_name) || (live && live.student_name) || student.studentName, [
      state.liveRows,
      state.rows,
      state.analyzedRows,
      [sheetRow, live]
    ]);
    setPeek({
      result: {
        ...(live || {}),
        ...(sheetRow || {}),
        id: live && live.id,
        image_names: imageNames,
        ocr_text: (sheetRow && sheetRow.ocr_text) || (live && live.ocr_text) || '',
        ocr_status: (sheetRow && sheetRow.ocr_status) || (live && live.ocr_status) || '',
        ocr_unclear_ratio: sheetRow && sheetRow.ocr_unclear_ratio != null
          ? sheetRow.ocr_unclear_ratio
          : (live ? live.ocr_unclear_ratio : null)
      },
      canSave: !!(live && live.id)
    });
  };

  const applyPeekSave = (next) => {
    setPeek((prev) => (prev ? { ...prev, result: next } : prev));
    setState((prev) => {
      if (!prev.sheet) return prev;
      const patch = (row) => {
        if (!row || !samePerson(row.student_name, next.student_name)) return row;
        return {
          ...row,
          table_comments: next.table_comments,
          comments: next.comments,
          marks: next.marks,
          comments_custom: next.comments_custom,
          ocr_text: next.ocr_text,
          ocr_status: next.ocr_status,
          ocr_unclear_ratio: next.ocr_unclear_ratio
        };
      };
      const rows = (prev.rows || []).map(patch);
      const liveRows = (prev.liveRows || []).map((row) => (row && row.id === next.id ? { ...row, ...next } : patch(row)));
      return { ...prev, rows, liveRows, sheet: buildSheet(rows, liveRows) };
    });
  };

  const showStatement = (event, key) => {
    const text = statements.get(key);
    if (!text) {
      setHoverTip(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 380;
    let x = rect.left;
    if (x + width > window.innerWidth - 12) x = Math.max(12, window.innerWidth - width - 12);
    setHoverTip({ text, x, y: rect.bottom + 6 });
  };

  const openQuestionEditor = (key) => {
    if (!rubricText) return;
    setHoverTip(null);
    setRubricFocus(key);
  };

  const saveRubricBody = async (next) => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${window.API_BASE_URL}/rubrics/${folder.id}/content`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ content: next })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
    setRubricText(next);
    setStatements(rubricStatements(next));
  };

  const applyReview = (body) => {
    if (!body) return;
    setResultOk(!!body.result_ok);
    setRegradeRequests(Array.isArray(body.requests) ? body.requests : []);
    if (onReviewChange) onReviewChange(body);
  };

  const refreshReview = async () => {
    const token = localStorage.getItem('authToken');
    const res = await fetch(`${window.API_BASE_URL}/analyze/folders/${folder.id}/review`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.success) throw new Error(body.message || 'Could not refresh this folder');
    applyReview(body);
    return body;
  };

  const markResultOk = async () => {
    if (actionBusy || resultOk) return;
    setActionBusy('ok');
    setActionNote('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${window.API_BASE_URL}/analyze/folders/${folder.id}/result-ok`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.message || 'Could not mark this result');
      await refreshReview();
      setActionNote('Result marked OK. The folder button is now green.');
    } catch (err) {
      setActionNote(err.message || 'Could not mark this result');
    } finally {
      setActionBusy('');
    }
  };

  const toggleStudent = (key) => {
    setSelectedStudents((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleQuestion = (key) => {
    setSelectedQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setAllStudents = (on) => {
    const keys = sheet ? sheet.students.map((student) => student.key) : [];
    setSelectedStudents(on ? new Set(keys) : new Set());
  };

  const setAllQuestions = (on) => {
    const keys = sheet ? sheet.classStats.map((question) => question.key) : [];
    setSelectedQuestions(on ? new Set(keys) : new Set());
  };

  const attachAudioFile = (file) => {
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    const url = URL.createObjectURL(file);
    audioUrlRef.current = url;
    setAudioFile(file);
    setAudioUrl(url);
    setAudioError('');
  };

  const startRecording = async () => {
    setAudioError('');
    if (!window.isSecureContext) {
      setAudioError('Microphone recording needs HTTPS or localhost. Open the app on a secure address and try again.');
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === 'undefined') {
      setAudioError('This browser cannot record audio.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      recordChunksRef.current = [];
      let mimeType = '';
      if (typeof MediaRecorder.isTypeSupported === 'function') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mimeType = 'audio/webm;codecs=opus';
        else if (MediaRecorder.isTypeSupported('audio/webm')) mimeType = 'audio/webm';
        else if (MediaRecorder.isTypeSupported('audio/mp4')) mimeType = 'audio/mp4';
        else if (MediaRecorder.isTypeSupported('audio/ogg')) mimeType = 'audio/ogg';
      }
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) recordChunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blobType = recorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(recordChunksRef.current, { type: blobType });
        if (recordStreamRef.current) {
          recordStreamRef.current.getTracks().forEach((track) => track.stop());
          recordStreamRef.current = null;
        }
        if (recordTimerRef.current) {
          clearInterval(recordTimerRef.current);
          recordTimerRef.current = null;
        }
        recorderRef.current = null;
        setRecording(false);
        if (!blob.size) {
          setAudioError('Recording was empty. Please try again.');
          return;
        }
        const ext = blobType.includes('mp4') ? 'm4a' : blobType.includes('ogg') ? 'ogg' : 'webm';
        attachAudioFile(new File([blob], `voice-note.${ext}`, { type: blobType.split(';')[0] }));
      };
      recorder.start(250);
      setRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = setInterval(() => setRecordSecs((secs) => secs + 1), 1000);
    } catch (err) {
      releaseRecording();
      setRecording(false);
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setAudioError('Microphone permission was denied. Allow the mic in the browser, then try again.');
      } else if (name === 'NotFoundError') {
        setAudioError('No microphone was found on this device.');
      } else {
        setAudioError((err && err.message) || 'Could not access the microphone.');
      }
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
  };

  const submitRegrade = async () => {
    if (actionBusy || recording) return;
    if (regradeRequests.length >= 20) {
      setActionNote('This folder already has 20 re-grade requests.');
      return;
    }
    const note = noteText.trim();
    if (!selectedStudents.size || !selectedQuestions.size || (!audioFile && !note)) {
      setActionNote('Select students, select questions, and add a message or a voice note before submitting.');
      return;
    }
    setActionBusy('submit');
    setActionNote('');
    try {
      const picked = (sheet ? sheet.students : []).filter((student) => selectedStudents.has(student.key));
      const form = new FormData();
      form.append('students', JSON.stringify(picked.map((student) => ({
        key: student.key,
        roll: student.roll,
        name: student.name,
        studentName: student.studentName
      }))));
      form.append('questions', JSON.stringify([...selectedQuestions]));
      if (note) form.append('note', note);
      if (audioFile) form.append('audio', audioFile);
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${window.API_BASE_URL}/analyze/folders/${folder.id}/regrade-requests`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.message || 'Could not submit the re-grade');
      setSelectedStudents(new Set());
      setSelectedQuestions(new Set());
      setNoteText('');
      setAudioFile(null);
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = '';
      }
      setAudioUrl('');
      await refreshReview();
      const slot = body.request && body.request.slot;
      setActionNote(slot ? `Re-Grade ${slot} submitted. It now appears on the folder card.` : 'Re-grade submitted.');
    } catch (err) {
      setActionNote(err.message || 'Could not submit the re-grade');
    } finally {
      setActionBusy('');
    }
  };

  const studentKeys = sheet ? sheet.students.map((student) => student.key) : [];
  const questionKeys = sheet ? sheet.classStats.map((question) => question.key) : [];
  const allStudentsOn = studentKeys.length > 0 && studentKeys.every((key) => selectedStudents.has(key));
  const someStudentsOn = studentKeys.some((key) => selectedStudents.has(key));
  const allQuestionsOn = questionKeys.length > 0 && questionKeys.every((key) => selectedQuestions.has(key));
  const someQuestionsOn = questionKeys.some((key) => selectedQuestions.has(key));
  const classQueryText = classQuery.trim().toLowerCase();
  const openFolder = classFolders.find((item) => String(item.id) === String(folder.id)) || folder;
  const sheetLabel = classSheetLabel(openFolder) || title;
  const classChoices = classFolders
    .filter((item) => item && item.type !== 'mcq' && String(item.id) !== String(folder.id))
    .map((item) => ({ folder: item, label: classSheetLabel(item) }))
    .filter((item) => item.label && (!classQueryText || item.label.toLowerCase().includes(classQueryText)))
    .sort((a, b) => a.label.localeCompare(b.label))
    .slice(0, 12);
  const openClassSheet = (next) => {
    setClassQuery('');
    setClassMenuOpen(false);
    if (next && onSwitchFolder && String(next.id) !== String(folder.id)) onSwitchFolder(next);
  };
  const canSubmit = !actionBusy && !recording && regradeRequests.length < 20
    && selectedStudents.size > 0 && selectedQuestions.size > 0 && !!(audioFile || noteText.trim());
  const recordLabel = recording
    ? `Stop ${recordSecs}s`
    : (audioFile ? 'Record again' : 'Record Voice');

  const stickyHead = {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: '#e2e8f0',
    color: '#0f172a',
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: '0.01em',
    padding: '8px 10px',
    border: '1px solid #cbd5e1',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    height: 64,
    boxSizing: 'border-box'
  };
  const pctHead = {
    position: 'sticky',
    top: 64,
    zIndex: 3,
    background: '#f8fafc',
    color: '#334155',
    fontWeight: 600,
    fontSize: 12,
    padding: '6px 10px',
    border: '1px solid #e2e8f0',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    height: 32,
    boxSizing: 'border-box'
  };
  const rankHead = {
    ...pctHead,
    top: 96,
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0'
  };
  const NAME_W = 292;
  const TOTAL_W = 78;
  const PCT_W = 72;
  const pinLeft = (left, width, zIndex) => ({
    position: 'sticky',
    left,
    width,
    minWidth: width,
    maxWidth: width,
    zIndex,
    boxSizing: 'border-box'
  });
  const nameCell = {
    ...pinLeft(0, NAME_W, 2),
    background: '#fff',
    textAlign: 'left',
    padding: '7px 10px',
    border: '1px solid #d1d5db',
    fontSize: 13
  };
  const totalCell = {
    ...pinLeft(NAME_W, TOTAL_W, 2),
    background: '#fff',
    border: '1px solid #e5e7eb',
    textAlign: 'center',
    fontWeight: 700,
    padding: '7px 8px',
    fontSize: 13
  };
  const percentCell = {
    ...pinLeft(NAME_W + TOTAL_W, PCT_W, 2),
    background: '#fff',
    border: '1px solid #e5e7eb',
    textAlign: 'center',
    fontWeight: 700,
    padding: '7px 8px',
    fontSize: 13
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        flexWrap: 'nowrap',
        overflowX: 'auto'
      }}>
        <div title={sheetLabel} style={{ fontWeight: 700, fontSize: 16, color: '#0f172a', letterSpacing: '-0.01em', whiteSpace: 'nowrap', flex: '0 0 auto' }}>{sheetLabel}</div>
        <div style={{ color: '#6b7280', fontSize: 13, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
          {state.status === 'ok' ? `${sheet.students.length} students` : 'Question report'}
          {' · '}{GRADING_MACHINE_LEGEND}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', marginLeft: 8, flex: '1 1 auto', minWidth: 0 }}>
          <div ref={classBoxRef} style={{ position: 'relative', flex: '0 0 240px' }}>
            <input
              type="text"
              value={classQuery}
              onChange={(event) => {
                setClassQuery(event.target.value);
                setClassMenuOpen(true);
              }}
              onFocus={() => setClassMenuOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setClassMenuOpen(false);
                  return;
                }
                if (event.key === 'Enter' && classChoices[0]) {
                  event.preventDefault();
                  openClassSheet(classChoices[0].folder);
                }
              }}
              placeholder="Open another class"
              aria-label="Open another class result sheet"
              style={{
                width: '100%',
                height: 34,
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                padding: '0 10px',
                font: 'inherit',
                fontSize: 13,
                color: '#0f172a',
                background: '#fff',
                boxSizing: 'border-box'
              }}
            />
            {classMenuOpen && classMenuPos && (
              <div
                style={{
                  position: 'fixed',
                  top: classMenuPos.top,
                  left: classMenuPos.left,
                  zIndex: 30,
                  width: classMenuPos.width,
                  maxHeight: 280,
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.14)'
                }}
              >
                {classChoices.length === 0 ? (
                  <div style={{ padding: '8px 10px', fontSize: 13, color: '#64748b' }}>No matching class</div>
                ) : classChoices.map((item) => (
                  <button
                    key={item.folder.id}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => openClassSheet(item.folder)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      background: '#fff',
                      color: '#0f172a',
                      padding: '8px 10px',
                      font: 'inherit',
                      fontSize: 13,
                      lineHeight: 1.35,
                      whiteSpace: 'normal',
                      cursor: 'pointer'
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={markResultOk}
            disabled={!!actionBusy || resultOk}
            title={resultOk ? 'This result is already marked OK' : 'Mark this result verified. The folder card Result button turns green.'}
            style={{
              height: 34,
              flex: '0 0 auto',
              border: '1px solid #047857',
              background: resultOk ? '#047857' : '#fff',
              color: resultOk ? '#fff' : '#047857',
              borderRadius: 8,
              padding: '0 12px',
              fontWeight: 600,
              fontSize: 13,
              cursor: resultOk || actionBusy ? 'default' : 'pointer',
              opacity: actionBusy === 'ok' ? 0.7 : 1
            }}
          >
            {actionBusy === 'ok' ? 'Saving…' : 'Result is OK'}
          </button>
          <input
            type="text"
            value={noteText}
            onChange={(event) => setNoteText(event.target.value)}
            maxLength={2000}
            placeholder="Message for this re-grade. It can go with the voice note, or on its own."
            aria-label="Re-grade message"
            disabled={!!actionBusy}
            style={{
              flex: '1 1 180px',
              minWidth: 140,
              width: 220,
              height: 34,
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              padding: '0 10px',
              font: 'inherit',
              fontSize: 13,
              color: '#0f172a',
              background: '#fff'
            }}
          />
          <button
            type="button"
            onClick={recording ? stopRecording : startRecording}
            disabled={!!actionBusy}
            title="Record a voice note for the students and questions you tick below"
            style={{
              height: 34,
              flex: '0 0 auto',
              border: recording ? '1px solid #b91c1c' : '1px solid #cbd5e1',
              background: recording ? '#b91c1c' : '#fff',
              color: recording ? '#fff' : '#334155',
              borderRadius: 8,
              padding: '0 12px',
              fontWeight: 600,
              fontSize: 13,
              cursor: actionBusy ? 'default' : 'pointer'
            }}
          >
            {recordLabel}
          </button>
          <button
            type="button"
            onClick={submitRegrade}
            disabled={!canSubmit}
            title={regradeRequests.length >= 20
              ? 'This folder already has 20 re-grade requests'
              : 'Submit the ticked students, ticked questions, and the message or voice note'}
            style={{
              height: 34,
              flex: '0 0 auto',
              border: '1px solid transparent',
              background: canSubmit ? '#0f172a' : '#e2e8f0',
              color: canSubmit ? '#fff' : '#64748b',
              borderRadius: 8,
              padding: '0 12px',
              fontWeight: 600,
              fontSize: 13,
              cursor: canSubmit ? 'pointer' : 'default'
            }}
          >
            {actionBusy === 'submit' ? 'Submitting…' : `Submit Re-Grade${regradeRequests.length ? ` (${regradeRequests.length}/20)` : ''}`}
          </button>
          {audioUrl && (
            <audio controls src={audioUrl} style={{ height: 32 }} />
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginLeft: 'auto',
            flex: '0 0 auto',
            whiteSpace: 'nowrap',
            border: '1px solid #d1d5db',
            background: '#fff',
            borderRadius: 8,
            padding: '6px 12px',
            fontWeight: 700,
            cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div>
      {(actionNote || audioError) && (
        <div style={{
          padding: '6px 16px',
          background: audioError ? '#fef2f2' : '#f0fdf4',
          color: audioError ? '#b91c1c' : '#166534',
          fontSize: 13,
          fontWeight: 600,
          borderBottom: '1px solid #e5e7eb'
        }}>
          {audioError || actionNote}
        </div>
      )}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {state.status === 'loading' && <div style={{ color: '#4b5563' }}>Loading marks…</div>}
        {state.status !== 'loading' && state.status !== 'ok' && (
          <div style={{ color: state.status === 'error' ? '#b91c1c' : '#4b5563' }}>{state.message}</div>
        )}
        {state.status === 'ok' && (
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, background: '#fff', minWidth: '100%' }}>
            <thead>
              <tr>
                <th
                  onClick={() => setSort(null)}
                  title="Roll-number order. The checkboxes select students and questions for a re-grade."
                  style={{ ...stickyHead, ...pinLeft(0, NAME_W, 5), textAlign: 'left', top: 0, cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <span>Roll / Name</span>
                    <span style={{ display: 'inline-flex', gap: 8 }} onClick={(event) => event.stopPropagation()}>
                      <TriCheckbox
                        checked={allStudentsOn}
                        indeterminate={someStudentsOn && !allStudentsOn}
                        onChange={(event) => setAllStudents(event.target.checked)}
                        title="Select every student for re-grade"
                        label="Students"
                      />
                      <TriCheckbox
                        checked={allQuestionsOn}
                        indeterminate={someQuestionsOn && !allQuestionsOn}
                        onChange={(event) => setAllQuestions(event.target.checked)}
                        title="Select every question for re-grade"
                        label="Questions"
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => toggleSort('total')}
                  title="Sort students by total marks"
                  style={{ ...stickyHead, ...pinLeft(NAME_W, TOTAL_W, 5), top: 0, cursor: 'pointer' }}
                >
                  Total{sortArrow('total')}
                </th>
                <th
                  onClick={() => toggleSort('percent')}
                  title="Sort students by overall percentage"
                  style={{ ...stickyHead, ...pinLeft(NAME_W + TOTAL_W, PCT_W, 5), top: 0, cursor: 'pointer' }}
                >
                  %{sortArrow('percent')}
                </th>
                {sheet.classStats.map((q) => {
                  const band = PERCENT_BANDS[percentBand(q)];
                  return (
                    <th
                      key={q.key}
                      onClick={() => openQuestionEditor(q.key)}
                      onMouseEnter={(event) => showStatement(event, q.key)}
                      onMouseLeave={() => setHoverTip(null)}
                      title="Edit this question in the rubric"
                      style={{
                        ...stickyHead,
                        cursor: rubricText ? 'pointer' : 'default',
                        background: band ? band.header : '#e2e8f0',
                        color: band ? band.color : '#0f172a',
                        border: band ? `1px solid ${band.border}` : '1px solid #cbd5e1'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <span>{q.label}</span>
                        <span onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedQuestions.has(q.key)}
                            onChange={() => toggleQuestion(q.key)}
                            title={`Include ${q.label} in the re-grade`}
                            style={{ width: 15, height: 15, margin: 0, accentColor: '#0f172a', cursor: 'pointer' }}
                          />
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
              <tr>
                <th style={{ ...pctHead, ...pinLeft(0, NAME_W, 5), textAlign: 'left', top: 64 }}>Class % / Q.Comments</th>
                <th style={{ ...pctHead, ...pinLeft(NAME_W, TOTAL_W, 5), top: 64 }}>—</th>
                <th style={{ ...pctHead, ...pinLeft(NAME_W + TOTAL_W, PCT_W, 5), top: 64 }}>{fmtPct(sheet.classObtained, sheet.classMax)}</th>
                {sheet.classStats.map((q) => (
                  <th
                    key={q.key}
                    onClick={() => setQuestionFocus(q.key)}
                    title={`Comments for ${q.label}, all students`}
                    style={{ ...pctHead, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    {fmtPct(q.obtained, q.max)}
                  </th>
                ))}
              </tr>
              <tr>
                <th style={{ ...rankHead, ...pinLeft(0, NAME_W, 5), textAlign: 'left' }}>
                  <button
                    type="button"
                    onClick={() => toggleSort('roll')}
                    title="Sort by roll letter, then number. Click again to reverse."
                    style={{
                      border: '1px solid #cbd5e1',
                      background: sort && sort.key === 'roll' ? '#0f172a' : '#fff',
                      color: sort && sort.key === 'roll' ? '#fff' : '#334155',
                      borderRadius: 6,
                      padding: '3px 8px',
                      fontWeight: 800,
                      fontSize: 12,
                      cursor: 'pointer'
                    }}
                  >
                    Sort{sortArrow('roll')}
                  </button>
                </th>
                <th style={{ ...rankHead, ...pinLeft(NAME_W, TOTAL_W, 5) }}>—</th>
                <th style={{ ...rankHead, ...pinLeft(NAME_W + TOTAL_W, PCT_W, 5) }}>—</th>
                {sheet.classStats.map((q) => {
                  const band = PERCENT_BANDS[percentBand(q)];
                  return (
                    <th
                      key={q.key}
                      onClick={() => { if (q.rank != null) toggleSort(q.key); }}
                      title={q.rank == null ? undefined : `Sort students by ${q.label} marks`}
                      style={band
                        ? { ...rankHead, background: band.rank, color: band.rankColor, border: `1px solid ${band.border}`, cursor: 'pointer' }
                        : { ...rankHead, cursor: q.rank == null ? 'default' : 'pointer' }}
                    >
                      {q.rank == null ? '—' : q.rank}{sortArrow(q.key)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {shownStudents.map((student, index) => (
                <tr key={student.key} style={{ background: index % 2 ? '#f9fafb' : '#fff' }}>
                  <td
                    style={{ ...nameCell, background: index % 2 ? '#f9fafb' : '#fff' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={selectedStudents.has(student.key)}
                        onChange={() => toggleStudent(student.key)}
                        title={`Include ${student.name} in the re-grade`}
                        style={{ width: 15, height: 15, marginTop: 3, accentColor: '#0f172a', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <button
                        type="button"
                        onClick={() => openStudentComments(student)}
                        title="View this student's comments"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          textAlign: 'left',
                          cursor: 'pointer',
                          font: 'inherit'
                        }}
                      >
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{student.roll || '—'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: '#334155' }}>{student.name}</span>
                          <GradingMachineBadge host={student.row && student.row.grading_host} />
                        </div>
                      </button>
                    </div>
                  </td>
                  <td style={{ ...totalCell, background: index % 2 ? '#f9fafb' : '#fff' }}>
                    {fmtNum(student.obtained)}
                  </td>
                  <td style={{ ...percentCell, background: index % 2 ? '#f9fafb' : '#fff' }}>
                    {fmtPct(student.obtained, student.max)}
                  </td>
                  {sheet.classStats.map((q) => {
                    const cell = student.byQuestion.get(q.key);
                    return (
                      <td key={q.key} style={{
                        border: '1px solid #e5e7eb',
                        textAlign: 'center',
                        padding: '7px 8px',
                        fontSize: 13,
                        fontVariantNumeric: 'tabular-nums'
                      }}>
                        {cell ? fmtNum(cell.obtained) : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {hoverTip && (
        <div
          style={{
            position: 'fixed',
            left: hoverTip.x,
            top: hoverTip.y,
            zIndex: 10001,
            maxWidth: 380,
            background: '#111827',
            color: '#fff',
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.45,
            boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
            pointerEvents: 'none'
          }}
        >
          {hoverTip.text}
        </div>
      )}
      {questionFocus && sheet && (() => {
        const stat = sheet.classStats.find((q) => q.key === questionFocus);
        if (!stat) return null;
        const statement = statements.get(stat.key);
        return (
          <div
            onClick={() => setQuestionFocus(null)}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 10070,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                background: '#fff',
                color: '#1f2937',
                borderRadius: 16,
                width: 'min(920px, calc(100vw - 48px))',
                maxHeight: 'calc(100vh - 48px)',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px', borderBottom: '1px solid #e5e7eb' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#14532d' }}>
                    {stat.label} comments
                    <span style={{ marginLeft: 10, fontSize: 14, color: '#166534' }}>{fmtPct(stat.obtained, stat.max)}</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                    {shownStudents.length} students
                  </div>
                  {statement && (
                    <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.45, color: '#1f2937' }}>{statement}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setQuestionFocus(null)}
                  className="modal-close-btn"
                >
                  <i className="fa-solid fa-times"></i> Close
                </button>
              </div>
              <div style={{ overflowY: 'auto', padding: '8px 12px 16px' }}>
                {shownStudents.map((student, index) => {
                  const cell = student.byQuestion.get(stat.key);
                  return (
                    <div
                      key={student.key}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '220px 88px 1fr',
                        gap: 12,
                        alignItems: 'start',
                        padding: '10px 8px',
                        borderBottom: '1px solid #f3f4f6',
                        background: index % 2 ? '#f9fafb' : '#fff'
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => openStudentComments(student)}
                        title="View this student's full comments"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: 0,
                          textAlign: 'left',
                          cursor: 'pointer',
                          font: 'inherit'
                        }}
                      >
                        <div style={{ fontWeight: 800, color: '#1d4ed8' }}>{student.roll || '—'}</div>
                        <div style={{ color: '#1e3a8a', textDecoration: 'underline' }}>{student.name}</div>
                      </button>
                      <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', paddingTop: 2 }}>
                        {cell ? `${fmtNum(cell.obtained)} / ${fmtNum(cell.max)}` : '—'}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45, fontSize: 14 }}>
                        {cell && cell.deduction ? cell.deduction : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      {peek && (
        <StudentResultView
          key={`${peek.result.id || ''}|${peek.result.student_name || ''}`}
          result={peek.result}
          rubricText={rubricText}
          folderId={folder.id}
          canSave={peek.canSave}
          onClose={() => setPeek(null)}
          onUpdated={applyPeekSave}
          onRubricSaved={(full) => {
            setRubricText(full);
            setStatements(rubricStatements(full));
          }}
        />
      )}
      {rubricFocus && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10050,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: '#fff',
              color: '#1f2937',
              borderRadius: 16,
              width: 'min(1200px, calc(100vw - 48px))',
              height: 'calc(100vh - 48px)',
              display: 'flex',
              flexDirection: 'column',
              padding: '18px 20px 14px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
              <strong style={{ fontSize: 16, color: '#059669' }}>
                Rubric — {title} — {formatQuestionLabel(rubricFocus)}
              </strong>
              <button
                type="button"
                onClick={() => setRubricFocus(null)}
                title="Close"
                style={{
                  background: 'transparent',
                  color: '#6b7280',
                  border: 'none',
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: '0 4px'
                }}
              >
                ×
              </button>
            </div>
            <RubricQuestionPanel
              content={rubricText}
              subject={rubricSubject}
              focusKey={rubricFocus}
              onSave={saveRubricBody}
            />
          </div>
        </div>
      )}
      {showGuide && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10100,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="result-sheet-guide-title"
            dir="rtl"
            style={{
              background: '#fff',
              color: '#1f2937',
              borderRadius: 16,
              width: 'min(640px, calc(100vw - 48px))',
              padding: '18px 20px 16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              fontFamily: '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Nafees Nastaleeq", serif'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <h2
                id="result-sheet-guide-title"
                style={{ margin: 0, fontSize: 22, lineHeight: 1.8, fontWeight: 800, color: '#14532d' }}
              >
                ضروری رہنمائی:
              </h2>
              <button
                type="button"
                onClick={() => setShowGuide(false)}
                className="modal-close-btn"
                title="Close"
                aria-label="Close"
                style={{ flexShrink: 0 }}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <p style={{ margin: '12px 0 0', fontSize: 18, lineHeight: 2.1, textAlign: 'right' }}>
              اگر آپ کو کسی سٹوڈنٹ کی ٹرانسکرپٹ (OCR) درست نہ لگے یا اس میں کوئی غلطی نظر آئے، تو اس مخصوص سوال کا سکرین شاٹ لے کر کسی بھی AI ماڈل (جیسے ChatGPT یا Gemini) سے اس کا نیا OCR نکال لیں۔ اس کے بعد یہاں غلط ٹرانسکرپٹ کی جگہ نیا درست متن پیسٹ کر کے <strong>Save</strong> کر دیں۔
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 16 }}>
              <button
                type="button"
                autoFocus
                onClick={() => setShowGuide(false)}
                style={{
                  background: '#059669',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 22px',
                  fontWeight: 800,
                  fontSize: 18,
                  lineHeight: 1.8,
                  cursor: 'pointer',
                  fontFamily: 'inherit'
                }}
              >
                سمجھ آ گئی
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
